/**
 * Per-account recovery records for saved board activity whose alert request
 * lacks a durable outbox acknowledgement. Records contain only source ids,
 * never content or recipient lists, and retries are always a user tap.
 */
const STORAGE_KEY = 'ldg_pending_board_alerts:v1';
const MAX_PENDING_PER_ACCOUNT = 50;

export type PendingBoardNotificationLane =
  | 'bagGroup'
  | 'compBoard'
  | 'parentBoard'
  | 'jrStaffBoard'
  | 'compParentsBoard';

export interface PendingBoardNotification {
  uid: string;
  lane: PendingBoardNotificationLane;
  primaryId: string;
  secondaryId: string;
  createdAt: number;
}

interface StoredPendingBoardNotifications {
  version: 1;
  alerts: PendingBoardNotification[];
}

function safeId(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= 512 && !value.includes('/');
}

function valid(value: unknown): value is PendingBoardNotification {
  if (!value || typeof value !== 'object') return false;
  const row = value as Partial<PendingBoardNotification>;
  return safeId(row.uid)
    && ['bagGroup', 'compBoard', 'parentBoard', 'jrStaffBoard', 'compParentsBoard'].includes(row.lane ?? '')
    && safeId(row.primaryId)
    && typeof row.secondaryId === 'string'
    && (row.secondaryId === '' || safeId(row.secondaryId))
    && typeof row.createdAt === 'number'
    && Number.isFinite(row.createdAt);
}

function readAll(): PendingBoardNotification[] {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return [];
    const stored = JSON.parse(raw) as StoredPendingBoardNotifications;
    return stored?.version === 1 && Array.isArray(stored.alerts) ? stored.alerts.filter(valid) : [];
  } catch {
    return [];
  }
}

function writeAll(alerts: PendingBoardNotification[]): boolean {
  try {
    if (!globalThis.localStorage) return false;
    globalThis.localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, alerts }));
    return true;
  } catch {
    return false;
  }
}

function sameSource(a: PendingBoardNotification, b: PendingBoardNotification): boolean {
  return a.lane === b.lane && a.primaryId === b.primaryId && a.secondaryId === b.secondaryId;
}

/** Return only this account's rows. Other signed-in accounts stay recoverable. */
export function pendingBoardNotifications(uid: string, lane?: PendingBoardNotificationLane, primaryId?: string): PendingBoardNotification[] {
  if (!safeId(uid)) return [];
  return readAll().filter((row) => row.uid === uid
    && (!lane || row.lane === lane)
    && (!primaryId || row.primaryId === primaryId));
}

/** Keep one retry per source. A full queue retains earlier sources and reports failure to retain another. */
export function rememberPendingBoardNotification(row: PendingBoardNotification): boolean {
  if (!valid(row)) return false;
  const all = readAll();
  const mine = all.filter((existing) => existing.uid === row.uid);
  const withoutSame = mine.filter((existing) => !sameSource(existing, row));
  if (withoutSame.length >= MAX_PENDING_PER_ACCOUNT) return false;
  const others = all.filter((existing) => existing.uid !== row.uid);
  return writeAll([...others, ...withoutSame, row]);
}

export function clearPendingBoardNotification(row: Pick<PendingBoardNotification, 'uid' | 'lane' | 'primaryId' | 'secondaryId'>): boolean {
  return writeAll(readAll().filter((existing) => existing.uid !== row.uid || !sameSource(existing, row as PendingBoardNotification)));
}
